import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Cube3D } from "./Cube3D";
import { DEMO_LEADS, TONE } from "../../lib/demoLeads";

const SIGNALS = [
  { label: "Interesse · Pompa di calore", x: 24, y: 6 },
  { label: "Interesse · Fotovoltaico", x: 76, y: 10 },
  { label: "Interesse · Climatizzatore", x: 18, y: 46 },
  { label: "Interesse · Accumulo", x: 82, y: 42 },
];

const Signal = ({ s, i }) => (
  <motion.div
    className="absolute left-1/2 top-[34%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
    initial={{ left: `${s.x}%`, top: `${s.y}%`, opacity: 0 }}
    animate={{ opacity: [0, 1, 1, 0], left: [`${s.x}%`, "50%"], top: [`${s.y}%`, "34%"] }}
    transition={{ duration: 3.2, delay: i * 0.8, repeat: Infinity, repeatDelay: 0.4, ease: "easeIn" }}
  >
    <span className="whitespace-nowrap font-mono text-[10px] sm:text-[11px] px-2 py-1 rounded-md bg-[#0e1528]/90 border hairline text-slate-300">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4fc3f7] mr-1.5 align-middle" />{s.label}
    </span>
  </motion.div>
);

export const LeadCard = ({ lead, compact = false }) => {
  const t = TONE[lead.tone] || TONE.heat;
  return (
    <div className={`glass rounded-2xl ${compact ? "p-4" : "p-5"} w-full`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#ff5500] pulse-dot" />
          <span className="text-[13px] font-semibold text-white">Nuovo contatto qualificato</span>
        </div>
        <span className="font-mono text-[10px] text-slate-400">#{lead.id}</span>
      </div>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-[13px]">
        <dt className="text-slate-400">Settore</dt><dd className={`${t.text} font-medium`}>{lead.sector}</dd>
        <dt className="text-slate-400">Interesse</dt><dd className="text-slate-100">{lead.interest}</dd>
        <dt className="text-slate-400">Zona</dt><dd className="text-slate-100">{lead.zone}</dd>
        <dt className="text-slate-400">Stato</dt>
        <dd><span className="inline-flex items-center gap-1.5 text-[12px] px-2 py-0.5 rounded-full bg-[#ff5500]/12 text-[#ffb07f] border border-[#ff5500]/30">{lead.status}</span></dd>
      </dl>
    </div>
  );
};

export const HeroConsole = () => {
  const [i, setI] = useState(0);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-1, 1], [10, -10]), { stiffness: 60, damping: 15 });
  const ry = useSpring(useTransform(mx, [-1, 1], [-14, 14]), { stiffness: 60, damping: 15 });

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % DEMO_LEADS.length), 3400);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <div onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative w-full aspect-[4/5] sm:aspect-square max-w-[560px] mx-auto" data-testid="hero-console">
      <div className="absolute inset-[4%] rounded-full border hairline spin-slow">
        <span className="absolute -top-1 left-1/2 w-2 h-2 rounded-full bg-[#ff5500] shadow-[0_0_14px_#ff5500]" />
      </div>
      <div className="absolute inset-[20%] rounded-full border border-dashed border-[#4fc3f7]/20 spin-rev">
        <span className="absolute top-1/2 -right-1 w-1.5 h-1.5 rounded-full bg-[#4fc3f7] shadow-[0_0_12px_#4fc3f7]" />
      </div>
      <div className="absolute inset-[18%] bottom-[30%] top-[6%] rounded-full bg-[radial-gradient(circle,rgba(59,110,240,0.35),transparent_70%)] blur-2xl" />
      {SIGNALS.map((s, k) => <Signal key={k} s={s} i={k} />)}
      <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 800 }}
        className="absolute left-1/2 top-[34%] -translate-x-1/2 -translate-y-1/2">
        <div className="hidden sm:block"><Cube3D size={128} /></div>
        <div className="sm:hidden"><Cube3D size={92} /></div>
      </motion.div>
      <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[92%] sm:w-[74%] h-[190px]">
        <AnimatePresence initial={false}>
          <motion.div key={i} className="absolute inset-x-0 bottom-0"
            initial={{ opacity: 0, y: 24, scale: 0.96, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -18, scale: 0.97, filter: "blur(4px)" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <LeadCard lead={DEMO_LEADS[i]} compact />
          </motion.div>
        </AnimatePresence>
      </div>
      <span className="absolute right-[4%] top-[2%] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Esempio grafico</span>
    </div>
  );
};
